import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import { useMemoizedFn } from 'ahooks';
import CardSlot from '@/pages/intelligence/components/CardSlot';
import ChartsIndex from '@/pages/intelligence/components/ChartsComponents/ChartsIndex';
import ChartBubble, { TOP_CHARTS_TYPES } from '@/pages/intelligence/components/ChartBubble';
import useVisualis from './hooks/useVisualis';
import { moveZero } from '@/pages/intelligence/components/Chart/_config/topchartsChartUtils';
import { handleSerieSymbolConfigByDataSet } from '@intelligence/utils/echartsUtils';
import { computeRankAxisOverrides } from '@/pages/intelligence/utils/storeRankAxisUtils';
import { getEChartStaticOption } from './utils';
import { xAxisLabelFormatter, yAxisLabelFormatter } from './utils/formatters';
import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import { COMMON_DATE_FORMATTER } from '@/pages/intelligence/pages/TopCharts/_config/topChartsConst';
import { STATIC_OPTION } from '@/pages/intelligence/pages/TopCharts/_config/echarts';
import JumpToTopCharts from '@intelligence/pages/TopCharts/components/StoreRankDetailsCore/components/Visualis/components/JumpToTopCharts';

import type { IBaseProps } from '@/pages/intelligence/types/common';
import type { IEChartOption } from '@/pages/intelligence/types/echart';
import type { IStoreRankDetailsVisualisSourceData } from '@/pages/intelligence/types/storeRank';

export interface IVisualisOption {
  chartType?: ChartType;
  isAreaEChart: boolean;
  isYAxisInverse: boolean;
  isShowYAxisName?: boolean;
  yAxisName?: string;
  yAxisNamePadding?: number | number[];
  tooltipHeaderLeftIcon: string;
  tooltipHeaderLeftTitle: string;
  sourceData: IStoreRankDetailsVisualisSourceData[];
  sourceDataDimensions: string[];
  sourceDataDimensionNameFormatter?: (name: string) => string;
  dimensionIconFormatter?: (name: string) => string;
}

interface IVisualisProps extends IVisualisOption, IBaseProps {
  isLoading: boolean;
  title: string;
  selectedMode: boolean | 'single' | 'multiple';
}

export default function Visualis(props: IVisualisProps) {
  const {
    isLoading,
    title,
    chartType = ChartType.Line,
    isAreaEChart,
    isYAxisInverse,
    isShowYAxisName,
    yAxisName,
    yAxisNamePadding,
    tooltipHeaderLeftIcon,
    tooltipHeaderLeftTitle,
    sourceData,
    sourceDataDimensions,
    selectedMode = true,
    sourceDataDimensionNameFormatter,
    dimensionIconFormatter,
    entityType,
    currentFetchSource,
  } = props;

  const { isCN } = useIntlExtend();

  const { yAxisOption, legendOption, chartBubbleOption, dynamicLineOption } = useVisualis({
    isAreaEChart,
    isYAxisInverse,
    isShowYAxisName,
    yAxisName,
    yAxisNamePadding,
    sourceData,
    sourceDataDimensions,
    tooltipHeaderLeftIcon,
    tooltipHeaderLeftTitle,
    sourceDataDimensionNameFormatter,
    dimensionIconFormatter,
  });
  const isNodata = !(sourceDataDimensions?.length > 0 && sourceData?.length > 0);

  // console 实体保持原有 Y 轴区间，不作轴缩放
  const isConsole = entityType === 'console';

  const storeRankValues = useMemo(() => {
    if (!isYAxisInverse || isConsole) return [];
    const values: number[] = [];
    sourceData?.forEach((dataItem) => {
      sourceDataDimensions?.forEach((dimension) => {
        const value = Number(dataItem?.[dimension]);
        if (Number.isFinite(value)) {
          values.push(value);
        }
      });
    });
    return values;
  }, [isYAxisInverse, isConsole, sourceData, sourceDataDimensions]);

  const rankAxisOverrides = useMemo(() => {
    if (!isYAxisInverse || isConsole) return null;
    return computeRankAxisOverrides(storeRankValues, 1);
  }, [isYAxisInverse, isConsole, storeRankValues]);

  const initialLineOption = getEChartStaticOption({
    xAxisLabelFormatter,
    yAxisLabelFormatter,
  });

  const [lineOption, setLineOption] = useState<IEChartOption>(initialLineOption);

  const tooltipSerieNameFormatter = useMemoizedFn((name: string) => {
    const displayName = sourceDataDimensionNameFormatter?.(name) || name;
    const icon = dimensionIconFormatter?.(name);
    if (icon) {
      return `<img src="${icon}" alt="" style="display:inline-block;width:16px;height:16px;flex-shrink:0;vertical-align:middle;object-fit:contain;" /><span style="vertical-align:middle;margin-left:4px;">${displayName}</span>`;
    }
    return displayName;
  });

  const tooltipFormatter = useMemoizedFn((params: any[]): string => {
    const date = params?.[0]?.axisValue;
    const dateM = moment(date);
    const dateStr = dateM.isValid() ? dateM.format(COMMON_DATE_FORMATTER) : '';
    const paramsTemp = moveZero(params);
    const topChartsType = TOP_CHARTS_TYPES.STORE_RANK_DETAILS;
    const headerLeftIcon = chartBubbleOption?.headerLeftIcon || '';
    const headerLeftTitle = chartBubbleOption?.headerLeftTitle || '-';
    const headerRightTitle = dateStr;

    return ChartBubble({
      chartType,
      topChartsType,
      lineSourceIcon: headerLeftIcon,
      headerLeftIconClassName: 'bubble-title-flex-img-platform',
      secondTitleRight: headerLeftTitle,
      secondTitleLeft: headerRightTitle,
      serieNameFormatter: tooltipSerieNameFormatter,
      data: paramsTemp,
    });
  });

  useEffect(() => {
    const series = dynamicLineOption?.series;
    const xAxisData = dynamicLineOption?.xAxis?.data;
    const datasetDimensions = dynamicLineOption?.dataset?.dimensions;
    const datasetSource = dynamicLineOption?.dataset?.source;
    const yAxisInverse = yAxisOption?.inverse || false;
    const yAxisName = yAxisOption?.name || '';
    const yAxisNameLocation = yAxisOption?.nameLocation || 'end';
    const yAxisNamePadding = yAxisOption?.yAxisNamePadding || [0, 5, 0, 0];
    const legendNameFormatter = legendOption?.formatter;
    const gridBaseLeft = STATIC_OPTION?.grid?.left;
    const gridBaseBottom = (STATIC_OPTION?.grid?.bottom as number) ?? 45;
    const lineOptionTemp: IEChartOption = {
      ...lineOption,
      grid: {
        ...lineOption?.grid,
        left: !yAxisName ? gridBaseLeft : isCN ? gridBaseLeft : 35,
        bottom: dimensionIconFormatter ? gridBaseBottom + 20 : gridBaseBottom,
      },
      tooltip: {
        ...lineOption?.tooltip,
        // @ts-ignore
        formatter: tooltipFormatter,
        backgroundColor: 'rgba(0,0,0,0)'
      },
      // series: Array.isArray(series) ? series : [],
      series: handleSerieSymbolConfigByDataSet(series, datasetSource),
      xAxis: {
        ...lineOption?.xAxis,
        // @ts-ignore
        data: Array.isArray(xAxisData) ? xAxisData : [],
      },
      yAxis: {
        ...lineOption?.yAxis,
        inverse: yAxisInverse,
        name: yAxisName,
        nameTextStyle: {
          color: 'rgba(0, 0, 0, .45)',
          fontSize: 12,
          padding: yAxisNamePadding,
          align: 'right',
        },
        nameLocation: yAxisNameLocation,
        min: rankAxisOverrides ? rankAxisOverrides.min : (yAxisInverse ? 1 : 0),
        max: rankAxisOverrides?.max ?? undefined,
        // @ts-ignore;
        splitNumber: 5,
        // @ts-ignore;
        minInterval: 1,
        interval: rankAxisOverrides?.interval ?? undefined,
      },
      legend: {
        ...lineOption?.legend,
        formatter: legendNameFormatter,
        ...(legendOption?.textStyle ? { textStyle: legendOption.textStyle } : {}),
        selectedMode,
      },
      dataset: {
        ...lineOption?.dataset,
        dimensions: Array.isArray(datasetDimensions) ? datasetDimensions : [],
        source: Array.isArray(datasetSource) ? datasetSource : [],
      },
    };

    setLineOption(lineOptionTemp);
  }, [isCN, dynamicLineOption, yAxisOption, tooltipFormatter, legendOption]);

  return (
    <CardSlot
      title={isLoading ? '' : title}
      rightDom={<JumpToTopCharts entityType={entityType} currentFetchSource={currentFetchSource} />}
      iconList={[]}
    >
      <div className="card-line">
        <ChartsIndex
          chartLoading={isLoading}
          isNodata={isNodata}
          chartType={chartType}
          lineOption={lineOption}
        />
      </div>
    </CardSlot>
  );
}
